import { useEffect, useState } from 'react'

import { useT } from '../lib/i18n'
import { applyWaitingUpdate, onUpdateWaiting, updateWaiting } from '../lib/sw'

/**
 * The service-worker update prompt.
 *
 * It renders in flow at the top of the column rather than as an overlay: an
 * update is not urgent enough to cover anything, and a banner that pushes
 * content down is a banner people actually read.
 *
 * **In the column, and that is load-bearing.** It shipped as a sibling *above*
 * `<App />` in `main.tsx`, which put it above `PhoneFrame` — and the frame is
 * `height: 100dvh`. Measured at 390×844 with the banner up, the document grew to
 * 905px against an 844px viewport and the tab bar's bottom edge sat 61px below
 * the fold, so all four tabs were off-screen while the banner was showing. The
 * `shrink-0` here is the fingerprint of a component written to be a flex child
 * of the column: it was, and it was not mounted as one. `PhoneFrame` renders it
 * now, so every column gets it and none of them grows.
 *
 * Registration lives in `lib/sw.ts`, not here — a column unmounts on navigation
 * and re-registering the worker each time is not what anyone wants.
 *
 * SUAP-0106.
 */
export function UpdateBanner() {
  const { t } = useT()
  const [needRefresh, setNeedRefresh] = useState(updateWaiting)

  useEffect(() => onUpdateWaiting(() => setNeedRefresh(updateWaiting())), [])

  if (!needRefresh) return null

  return (
    // <aside>, not <div>: this sits above <main> in the column, so its text was
    // outside every landmark and axe's `region` rule fired on it — the same rule
    // and the same fix as the shell's header slot, found by the same check once
    // something finally rendered the banner. `complementary` rather than a second
    // `banner`: this is not the screen's header and must not compete with one.
    // SUAP-0113.
    // `data-update-banner` is how the checks find it. They used to look for
    // `aside:has-text("Suap")`, which couples the presence probe to the tag the
    // audit grades: turning this back into a <div> — the exact break SUAP-0113
    // fixed — made `check:a11y --banner` report "no update banner rendered"
    // instead of the axe `region` violation it exists to catch. Right exit code,
    // wrong cause. SUAP-0119.
    <aside
      data-update-banner
      className="flex shrink-0 items-center justify-between gap-3 border-b border-hairline bg-surface px-4 py-2"
    >
      <p className="min-w-0 text-sm text-ink">{t('update.available')}</p>
      <button
        type="button"
        onClick={() => void applyWaitingUpdate()}
        className="min-h-11 shrink-0 rounded-chip bg-ink px-4 text-sm font-medium text-ground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
      >
        {t('update.action')}
      </button>
    </aside>
  )
}
